import React, { FC, useEffect, useMemo } from 'react';
import { Control, FieldValues } from 'react-hook-form';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Typography } from '@altermaind/web/ui';
import { QuestionTypeEnum } from '@/swagger/constants/quiz-enums';
import { postQuizByIdNextCall } from '../../../state/api/postQuizByIdNextCall';
import { useEmployeeCompleteQuiz } from '../hook/useEmployeeCompleteQuiz';
import { AnswerFeedback } from './AnswerFeedback';
import { DynamicQuizSkeleton } from './DynamicQuizSkeleton';
import { MultipleChoiceAnswer } from './MultipleChoiceAnswer';
import { SingleChoiceAnswer } from './SingleChoiceAnswer';

type Props = {
  currentStep?: ReturnType<typeof useEmployeeCompleteQuiz>['currentStep'];
  progressiveSteps?: ReturnType<typeof useEmployeeCompleteQuiz>['progressiveSteps'];
  control: Control<FieldValues>;
  currentStepInPreview: boolean;
};

export const DynamicQuiz: FC<Props> = ({
  currentStep,
  control,
  currentStepInPreview,
  progressiveSteps,
}) => {
  const t = useTranslations('configuration.learning.employeeCompleteSurvey');
  const { loading: loadingPostQuizByIdNext } = postQuizByIdNextCall.hook();

  const currentStepNumber = useMemo(
    () => {
      const stepNumber = currentStep?.nextQuestion?.stepNumber ?? undefined;
      if (stepNumber !== undefined) {
        return stepNumber;
      }
      const lastStep = progressiveSteps?.[Object.keys(progressiveSteps ?? {}).length - 1];
      return (lastStep?.nextQuestion?.stepNumber ?? 0) + 1;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentStep?.nextQuestion?.stepNumber],
  );

  const totalSteps = useMemo(
    () => {
      const total = currentStep?.nextQuestion?.totalSteps ?? undefined;
      if (total !== undefined) {
        return total;
      }
      const lastStep = progressiveSteps?.[Object.keys(progressiveSteps ?? {}).length - 1];
      return lastStep?.nextQuestion?.totalSteps ?? 0;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentStep?.nextQuestion?.totalSteps],
  );

  /**
   * Generates the text of the current or previous question based on the current step and preview state.
   *
   * This function uses the `useMemo` hook to optimize performance by memoizing the result. It determines the question text
   * to display based on the `currentStep` and `currentStepInPreview` values. If the `currentStepInPreview` is true, it retrieves
   * the question text from the previous step. Otherwise, it retrieves the question text from the current step.
   *console.log(questionText); // Output: "Where do you live?"
   */
  const questionText = useMemo(
    () => {
      if (currentStepInPreview) {
        const previousStep = progressiveSteps?.[(currentStep?.nextQuestion?.stepNumber ?? 2) - 1];
        const previousQuestion = previousStep?.nextQuestion?.question.text ?? undefined;
        return previousQuestion ?? '';
      }

      const currentQuestion = currentStep?.nextQuestion?.question.text ?? undefined;
      if (currentQuestion !== undefined) {
        return currentQuestion;
      }
      return '';
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentStep?.nextQuestion?.stepNumber, currentStepInPreview],
  );

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, [currentStepInPreview]);

  return loadingPostQuizByIdNext ? (
    <DynamicQuizSkeleton />
  ) : (
    <Box>
      <Typography variant='h4' mb='xxs'>
        {questionText}
      </Typography>
      {totalSteps && (
        <Typography variant='p3/light' mb='s'>
          {t('stepXonY', {
            currentStep:
              currentStepInPreview && currentStepNumber > 1
                ? currentStepNumber - 1
                : currentStepNumber,
            totalSteps,
          })}
        </Typography>
      )}
      {/*
       * Use a key tied to questionId to force a remount when the question changes.
       * This prevents React from reusing the previous component instance (and any
       * react-hook-form field registration/state), avoiding stale values carrying
       * over from the previous question.
       */}
      {!currentStepInPreview &&
        currentStep?.nextQuestion?.question.type === QuestionTypeEnum.SINGLE_CHOICE && (
          <SingleChoiceAnswer
            key={`single-${currentStep?.nextQuestion?.question.id}`}
            currentStep={currentStep}
            control={control}
          />
        )}
      {!currentStepInPreview &&
        currentStep?.nextQuestion?.question.type === QuestionTypeEnum.MULTIPLE_CHOICE && (
          <MultipleChoiceAnswer
            key={`multi-${currentStep?.nextQuestion?.question.id}`}
            currentStep={currentStep}
            control={control}
          />
        )}
      {currentStepInPreview && currentStep?.answerResult && currentStep?.enrichment && (
        <AnswerFeedback
          answerResult={currentStep.answerResult}
          feedbackText={currentStep.enrichment}
        />
      )}
    </Box>
  );
};
